import styled from '@emotion/styled'
import { Site } from '@pubstack/common/src/site'
import { FC, useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useUpdateSite } from '~/api/app/sites.api'
import { Fonts } from '~/assets/style/fonts'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { CopyButton } from '~/components/CopyButton'
import { Flyout } from '~/components/Flyout'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { ConnectToPubstack } from '~/feature-components/connect/ConnectToPubstack'

const EditSiteWrapper = styled.div`
  ${Fonts.P1}
  & h2 {
    ${Fonts.H2}
    ${Fonts.colors.Jet}
    margin: 0;
    padding: 0;
  }

  display: flex;
  flex-direction: column;
  gap: 32px;
`

const TagInfoWrapper = styled.div`
  & h2 {
    margin-bottom: 16px;
  }
`

const EditSiteForm = styled.form`
  padding-right: 64px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  & ${Button} {
    align-self: flex-start;
  }
`

const EditActionsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 24px;
`

const InputIdWrapper = styled.div`
  display: flex;
  gap: 12px;

  & div {
    flex-grow: 1;
  }
`

export const SettingsSitesEditSiteFlyout: FC<{
  site: Site
  onCopyToClipBoard: (label: string) => unknown
}> = ({ site, onCopyToClipBoard }) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Site>({
    defaultValues: site,
  })
  const flyout = useGlobalFlyout()
  const user = useAuthenticatedUser()
  const formValues = useWatch({ control, defaultValue: site })

  const { mutateAsync: editSite } = useUpdateSite(user.currentScopeId, site.id, {
    successToast: (
      <>
        <strong>{formValues.name}</strong> was successfully updated.
      </>
    ),
    errorToast: (
      <>
        An error occurred while trying to edit <strong>{formValues.name}</strong>.
      </>
    ),
  })

  const saveChanges = async (site: Site) => {
    await editSite(site)
    flyout.close()
  }

  useEffect(() => {
    reset(site)
  }, [site])

  return (
    <Flyout.Content>
      <Flyout.Title>{site?.name}</Flyout.Title>
      <Flyout.Body>
        {site && (
          <EditSiteWrapper>
            <EditSiteForm>
              <h2>Global information</h2>
              <Input control={control} name={'name'} label={'Site name'} />
              <InputIdWrapper>
                <Input readOnly disabled control={control} name={'id'} label={'Site ID'} />
                <CopyButton onCopy={() => onCopyToClipBoard('Site ID')} contentText={site.id} trackingLabel={'common-settings-sites-site-id-copy'} />
              </InputIdWrapper>
            </EditSiteForm>
            <TagInfoWrapper>
              <h2>Connect to Pubstack</h2>
              {/* `analyticsOnly`: this flyout is where One's connect page sends a publisher who wants the
                  measurement tag without the ad management runtime, so it must not follow the media's product. */}
              <ConnectToPubstack site={site} trackingPrefix={'common-settings-sites'} analyticsOnly />
            </TagInfoWrapper>
          </EditSiteWrapper>
        )}
      </Flyout.Body>

      {isDirty && (
        <Flyout.Actions>
          <EditActionsWrapper>
            <Button onClick={() => reset(site)} iconName={'restore'} variant={'tertiary'} trackingLabel={'common-settings-sites-edition-restore'}>
              Restore
            </Button>
            <Button onClick={handleSubmit(saveChanges)} trackingLabel={'common-settings-sites-edition-save'}>
              Save changes
            </Button>
          </EditActionsWrapper>
        </Flyout.Actions>
      )}
    </Flyout.Content>
  )
}
